import { gql, useMutation } from "@apollo/client";
import Users from "components/users/Users";
import { ScopedRole__TenantScope } from "model/schema/globalTypes";
import { notNullOrUndefined } from "utils/typescript";
import {
  RemoveTenantUser,
  RemoveTenantUserVariables
} from "./schema/RemoveTenantUser";
import {
  UpdateUserRoles,
  UpdateUserRolesVariables
} from "./schema/UpdateUserRoles";
import {
  allUsersList,
  useQueryGetAllUsers,
  useQueryGetAvailableRoles
} from "./tenantUsersHook";

export const updateUserRoleMutation = gql`
  mutation UpdateUserRoles(
    $userName: String!
    $roles: [ScopedRole__TenantScope!]!
  ) {
    tenant {
      access {
        updateUserRoles(roles: $roles, userName: $userName) {
          name
          roles
        }
      }
    }
  }
`;
export const removeTenantUserMutation = gql`
  mutation RemoveTenantUser($name: String!) {
    users {
      deleteUser(name: $name)
    }
  }
`;
export default function TenantUsers() {
  const [
    loadUsers,
    { data: usersData, loading: loadingUsers, error: getUsersError }
  ] = useQueryGetAllUsers();

  const { data: roles, loading: loadingAvailableRoles } =
    useQueryGetAvailableRoles();

  const [
    updateUserRoles,
    {
      loading: updateUserRolesLoading,
      error: updateUserRolesError,
      reset: resetUpdateUserRoleError
    }
  ] = useMutation<UpdateUserRoles, UpdateUserRolesVariables>(
    updateUserRoleMutation,
    {
      awaitRefetchQueries: true,
      refetchQueries: [{ query: allUsersList }]
    }
  );

  const [
    removeUser,
    {
      error: removeUserError,
      reset: resetRemoveUserError,
      loading: removeUserLoading
    }
  ] = useMutation<RemoveTenantUser, RemoveTenantUserVariables>(
    removeTenantUserMutation,
    {
      refetchQueries: [{ query: allUsersList }]
    }
  );

  const allUsers = (usersData?.tenant.access.users || []).filter(
    notNullOrUndefined
  );

  const allRoles = roles?.tenant.access.availableRoles || [];

  const isLoading =
    loadingUsers ||
    loadingAvailableRoles ||
    updateUserRolesLoading ||
    removeUserLoading;

  const adminCount = allUsers.filter(m =>
    m.roles.includes(ScopedRole__TenantScope.Admin)
  ).length;

  return (
    <Users<ScopedRole__TenantScope>
      users={allUsers}
      allRoles={allRoles}
      loadUsers={loadUsers}
      loadingRoles={updateUserRolesLoading}
      onRoleUpdated={(userName, roles) => {
        updateUserRoles({ variables: { userName, roles } });
      }}
      onRemoveUser={userName => {
        removeUser({ variables: { name: userName } });
      }}
      resetUpdateUserRoleError={resetUpdateUserRoleError}
      updateUserRoleError={updateUserRolesError}
      removeUserError={removeUserError}
      resetRemoveUserError={resetRemoveUserError}
      isLoading={isLoading}
      error={getUsersError}
      title="Tenant Users"
      onRefresh={loadUsers}
      // SDL-27713 - Tenant Admin/UserManager Can Remove Users from Tenant
      // Just disable it for now; enable again when SDL-27713 is resolved
      disableDeleteUser={true}
      adminRole={ScopedRole__TenantScope.Admin}
      adminCount={adminCount}
      lastAdminWarning="You are about to remove the last admin role from all users. If you proceed, there will be no users with administrative privileges, which may prevent further management of user roles and permissions. Please ensure at least one user retains the admin role before continuing."
    />
  );
}
